/**
 * @fileoverview Reset password page.
 * Reads a reset token from the URL, validates the new password client-side,
 * and submits to the backend to complete the password reset.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { validatePassword } from '../utils/validation';
import PasswordInput from '../components/PasswordInput';

/**
 * Reset password page component.
 * Reads the ?token= query param, collects new password + confirmation,
 * validates client-side, and calls POST /api/auth/reset-password.
 * Handles expired (410) and invalid (404) token errors.
 *
 * @returns {JSX.Element} The reset password page
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const successRef = useRef(null);

  useEffect(() => {
    if (success) successRef.current?.focus();
  }, [success]);

  /**
   * Validate the password fields and return an errors object.
   *
   * @returns {Object} Map of field name to error message (empty if valid)
   */
  const validate = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const pwError = validatePassword(password);
      if (pwError) newErrors.password = pwError;
    }
    if (!confirmPassword) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    return newErrors;
  };

  /**
   * Handle form submission — validate and call the reset-password API.
   *
   * @param {React.FormEvent} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setApiError('');
    try {
      await api.resetPassword({
        token,
        password,
        confirm_password: confirmPassword,
      });
      setSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setApiError('This reset link has expired. Please request a new one.');
      } else if (status === 404) {
        setApiError('Invalid reset link. Please request a new one.');
      } else {
        setApiError(err.response?.data?.error || 'Password reset failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // No token provided — show error
  if (!token) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <a href="https://cogslog.com" aria-label="COGSLOG home">
              <img src="/logos/stacked/cogslog-stacked-tagline-color.svg" alt="COGSLOG" className="auth-logo" />
            </a>
            <h1 className="reset-password-title">Invalid Link</h1>
            <p className="reset-password-subtitle">
              No reset token found. Please request a new password reset.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/forgot-password')}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  // Success screen
  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <a href="https://cogslog.com" aria-label="COGSLOG home">
              <img src="/logos/stacked/cogslog-stacked-tagline-color.svg" alt="COGSLOG" className="auth-logo" />
            </a>
            <svg
              className="reset-password-icon reset-password-icon--success"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <h1 ref={successRef} tabIndex={-1} className="reset-password-title">Password Reset!</h1>
            <p className="reset-password-subtitle">
              Your password has been updated. You can now sign in with your new password.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary reset-password-submit"
            onClick={() => navigate('/login')}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="reset-password-title">Reset Password</h1>
          <p className="reset-password-subtitle">
            Choose a new password for your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form" noValidate>
          {apiError && (
            <div className="err-msg" role="alert">
              {apiError}
            </div>
          )}

          <div className="field">
            <label htmlFor="password" className="label">New Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors(prev => { const n = {...prev}; delete n.password; return n; });
                if (apiError) setApiError('');
              }}
              disabled={submitting}
              autoFocus
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
            />
            {errors.password && (
              <span id="password-error" className="err-msg">{errors.password}</span>
            )}
            <span className="hint">
              8+ characters, uppercase, lowercase, and a digit
            </span>
          </div>

          <div className="field">
            <label htmlFor="confirm_password" className="label">Confirm Password</label>
            <PasswordInput
              id="confirm_password"
              name="confirm_password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirm_password) setErrors(prev => { const n = {...prev}; delete n.confirm_password; return n; });
              }}
              disabled={submitting}
              aria-invalid={!!errors.confirm_password}
              aria-describedby={errors.confirm_password ? 'confirm_password-error' : undefined}
            />
            {errors.confirm_password && (
              <span id="confirm_password-error" className="err-msg">{errors.confirm_password}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary reset-password-submit"
            disabled={submitting}
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="reset-password-links">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/login')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
